import {
  ADMINISTRATION_ORGANIZATION_BILLING_CHANGE_SCOPES,
  ADMINISTRATION_ORGANIZATION_PEOPLE_CHANGE_SCOPES,
  type AdministrationOrganizationRealtimeScope,
  type AdministrationState,
} from "./types";

const settleRefreshes = async (requests: Promise<unknown>[]) => {
  const results = await Promise.allSettled(requests);
  if (results.some((result) => result.status === "rejected")) {
    throw new Error("One or more Administration organization refreshes failed.");
  }
};

export const hasLoadedAdministrationState = (state: AdministrationState) =>
  state.dashboardHydrated ||
  state.policiesHydrated ||
  state.organizationsHydrated ||
  Object.keys(state.organizationDetails).length > 0 ||
  Object.keys(state.organizationPeopleQueries).length > 0 ||
  Object.keys(state.organizationNetworkQueries).length > 0 ||
  Object.keys(state.organizationBillingQueries).length > 0 ||
  Object.keys(state.organizationActivityQueries).length > 0;

export const refreshAdministrationOrganizationScopes = async (
  state: AdministrationState,
  id: string,
  scopes: Iterable<AdministrationOrganizationRealtimeScope>,
) => {
  const requested = new Set(scopes);
  const requests: Promise<unknown>[] = [];

  if (requested.has("overview")) {
    if (state.organizationDetails[id]) requests.push(state.loadOrganizationDetail(id, true));
    if (state.organizationsHydrated) requests.push(state.loadOrganizations({}, true));
    if (state.dashboardHydrated) requests.push(state.loadDashboard(true));
  }
  if (requested.has("people")) {
    Object.values(state.organizationPeopleQueries)
      .filter((query) => query.id === id)
      .forEach(({ memberLimit, memberOffset, invitationLimit, invitationOffset }) => {
        requests.push(state.loadOrganizationPeople(id, { memberLimit, memberOffset, invitationLimit, invitationOffset }, true));
      });
  }
  if (requested.has("network")) {
    Object.values(state.organizationNetworkQueries)
      .filter((query) => query.id === id)
      .forEach(({ locationStatus, machineStatus, locationLimit, locationOffset, machineLimit, machineOffset }) => {
        requests.push(state.loadOrganizationNetwork(id, { locationStatus, machineStatus, locationLimit, locationOffset, machineLimit, machineOffset }, true));
      });
  }
  if (requested.has("billing")) {
    Object.values(state.organizationBillingQueries)
      .filter((query) => query.id === id)
      .forEach(({ limit, offset }) => {
        requests.push(state.loadOrganizationBilling(id, { limit, offset }, true));
      });
    Object.values(state.organizationChargeScopes)
      .filter((scope) => scope.organizationId === id)
      .forEach(({ chargeId }) => {
        requests.push(state.loadOrganizationCharge(id, chargeId, true));
      });
  }
  if (requested.has("activity")) {
    Object.values(state.organizationActivityQueries)
      .filter((query) => query.id === id)
      .forEach(({ domain, severity }) => {
        requests.push(state.loadOrganizationActivity(id, { domain, severity }, true));
      });
  }

  await settleRefreshes(requests);
};

export const refreshOrganizationActivity = async (state: AdministrationState, id: string) => {
  await Promise.allSettled(Object.values(state.organizationActivityQueries).filter((query) => query.id === id).map(({ domain, severity }) => state.loadOrganizationActivity(id, { domain, severity }, true)));
};

export const refreshOrganizationPeople = async (state: AdministrationState, id: string) => {
  await refreshAdministrationOrganizationScopes(state, id, ADMINISTRATION_ORGANIZATION_PEOPLE_CHANGE_SCOPES);
};

export const refreshOrganizationBilling = async (state: AdministrationState, id: string) => {
  await refreshAdministrationOrganizationScopes(state, id, ADMINISTRATION_ORGANIZATION_BILLING_CHANGE_SCOPES);
};

export const refreshLoadedAdministration = async (state: AdministrationState) => {
  await Promise.all([
    ...(state.dashboardHydrated ? [state.loadDashboard(true)] : []), ...(state.policiesHydrated ? [state.loadPolicies(true)] : []),
    ...(state.healthHydrated ? [state.loadHealth(true)] : []), ...(state.auditHydrated ? [state.loadAudit({}, true)] : []),
    ...(state.organizationsHydrated ? [state.loadOrganizations({}, true)] : []), ...Object.keys(state.organizationDetails).map((id) => state.loadOrganizationDetail(id, true)),
    ...Object.values(state.organizationPeopleQueries).map(({ id, memberLimit, memberOffset, invitationLimit, invitationOffset }) => state.loadOrganizationPeople(id, { memberLimit, memberOffset, invitationLimit, invitationOffset }, true)),
    ...Object.values(state.organizationNetworkQueries).map(({ id, locationStatus, machineStatus, locationLimit, locationOffset, machineLimit, machineOffset }) => state.loadOrganizationNetwork(id, { locationStatus, machineStatus, locationLimit, locationOffset, machineLimit, machineOffset }, true)),
    ...Object.values(state.organizationActivityQueries).map(({ id, domain, severity }) => state.loadOrganizationActivity(id, { domain, severity }, true)),
    ...Object.values(state.organizationBillingQueries).map(({ id, limit, offset }) => state.loadOrganizationBilling(id, { limit, offset }, true)),
  ]);
};
